import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import BookingForm from "@/components/BookingForm";
import { Users, MoveRight, Sparkles, Phone, Mail } from "lucide-react";
import { HOTLINE, EMAIL, WHATSAPP_CATALOG } from "@/lib/contact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Rooms() {
  const { t, lang } = useLang();
  const [rooms, setRooms] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API}/rooms`).then((res) => setRooms(res.data)).catch(() => {});
  }, []);

  const featured = rooms.find((r) => r.featured) || rooms[0];
  const others = rooms.filter((r) => r !== featured);

  return (
    <section id="stay" className="bg-white" data-testid="rooms-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Section header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <p className="eyebrow tag-line">{t.rooms.eyebrow}</p>
            <h2 className={`mt-4 text-4xl md:text-5xl font-display text-forest-700 tracking-tight leading-[1.05] ${lang==='bn'?'font-bn':''}`}
                data-testid="rooms-title">
              {t.rooms.title}
            </h2>
            <p className="mt-4 text-base text-slate-warm max-w-xl">{t.rooms.sub}</p>
            <div className="gold-rule mt-7" />
          </div>
        </div>

        {/* Featured Room (AC Couple Bed Room with AI Special Offer) */}
        {featured && <FeaturedRoom room={featured} t={t} lang={lang} onPick={(id) => { setSelected(id); document.getElementById("booking")?.scrollIntoView({ behavior: 'smooth' }); }} />}

        {/* Pricing table — full transparency */}
        <div className="mt-16 bg-cream-100 border border-forest-100 rounded-md overflow-hidden" data-testid="rooms-pricing-table">
          <div className="hidden md:grid grid-cols-12 px-7 py-4 border-b border-forest-100 bg-white">
            <div className="col-span-5 text-[10px] uppercase tracking-widest2 text-gold-600">Suite</div>
            <div className="col-span-2 text-[10px] uppercase tracking-widest2 text-gold-600">Capacity</div>
            <div className="col-span-3 text-[10px] uppercase tracking-widest2 text-gold-600">Standard / AI Offer</div>
            <div className="col-span-2 text-[10px] uppercase tracking-widest2 text-gold-600 text-right">Reserve</div>
          </div>
          {rooms.map((r) => (
            <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-0 px-7 py-6 border-b border-forest-100 last:border-b-0 items-center"
                 data-testid={`room-row-${r.id}`}>
              <div className="md:col-span-5 flex items-center gap-4">
                <img src={r.image_url} alt={r.name_en} className="w-16 h-16 object-cover rounded" />
                <div>
                  <div className={`text-base font-display text-forest-700 ${lang==='bn'?'font-bn':''}`}>
                    {lang === 'en' ? r.name_en : r.name_bn}
                  </div>
                  <div className="text-xs text-slate-warm mt-1 line-clamp-1">
                    {(r.amenities || []).slice(0,3).join(' · ')}
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 flex items-center gap-2 text-sm text-slate-warm">
                <Users className="w-4 h-4 text-gold-600" /> {r.capacity} {t.rooms.capacity}
              </div>
              <div className="md:col-span-3">
                {r.offer_price_bdt && r.offer_price_bdt < r.price_bdt ? (
                  <div className="flex items-baseline gap-3">
                    <span className="text-xl font-display text-forest-700">৳{r.offer_price_bdt.toLocaleString()}</span>
                    <span className="text-xs text-slate-warm line-through">৳{r.price_bdt.toLocaleString()}</span>
                  </div>
                ) : (
                  <span className="text-xl font-display text-forest-700">৳{r.price_bdt.toLocaleString()}</span>
                )}
                <div className="text-[10px] uppercase tracking-widest2 text-gold-600 mt-0.5">{t.rooms.per_night}</div>
              </div>
              <div className="md:col-span-2 md:text-right">
                <button onClick={() => { setSelected(r.id); document.getElementById("booking")?.scrollIntoView({ behavior: 'smooth' }); }}
                        data-testid={`room-inquire-${r.id}`}
                        className="group inline-flex items-center gap-1.5 text-sm text-forest-700 hover:text-gold-600 transition-colors">
                  {t.rooms.view} <MoveRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Booking widget */}
        <div id="booking" className="mt-24 grid lg:grid-cols-12 gap-10 items-start scroll-mt-24">
          <div className="lg:col-span-4">
            <p className="eyebrow tag-line">Booking</p>
            <h2 className={`mt-4 text-3xl md:text-4xl font-display text-forest-700 tracking-tight leading-tight ${lang==='bn'?'font-bn':''}`}>
              {t.booking.title}
            </h2>
            <p className="mt-4 text-sm text-slate-warm">{t.booking.sub}</p>
            <div className="mt-8 space-y-4">
              <ContactRow icon={Phone} label="Hotline" value={HOTLINE} href={`tel:${HOTLINE}`} testid="booking-side-phone" />
              <ContactRow icon={Mail} label="Email" value={EMAIL} href={`mailto:${EMAIL}`} testid="booking-side-email" />
              <a href={WHATSAPP_CATALOG} target="_blank" rel="noreferrer" data-testid="booking-side-catalog"
                 className="mt-4 inline-flex items-center gap-2 text-sm text-gold-600 hover:text-gold-700 transition-colors">
                <Sparkles className="w-4 h-4" /> {t.contact.catalog} <MoveRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
          <div className="lg:col-span-8">
            <BookingForm rooms={rooms} preselectRoomId={selected} />
          </div>
        </div>
      </div>
    </section>
  );
}

const FeaturedRoom = ({ room, t, lang, onPick }) => {
  const hasOffer = room.offer_price_bdt && room.offer_price_bdt < room.price_bdt;
  const off = hasOffer ? Math.round((1 - room.offer_price_bdt / room.price_bdt) * 100) : 0;

  return (
    <article className="grid lg:grid-cols-12 gap-0 border border-forest-100 rounded-md overflow-hidden bg-cream-50"
             data-testid={`featured-room-${room.id}`}>
      <div className="lg:col-span-7 relative group overflow-hidden">
        <img src={room.image_url} alt={room.name_en}
             className="w-full h-[400px] lg:h-full object-cover img-zoom" />
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest2 bg-cream-100 text-forest-700 px-3 py-1.5 rounded-full">
            {t.rooms.featured_strip}
          </span>
        </div>
        {hasOffer && (
          <div className="absolute top-4 right-4 offer-ribbon px-4 py-2 rounded-full text-[11px] uppercase tracking-widest2 flex items-center gap-2 shadow-lg"
               data-testid="ai-offer-ribbon">
            <Sparkles className="w-3.5 h-3.5" /> {t.rooms.offer_label} · −{off}%
          </div>
        )}
      </div>
      <div className="lg:col-span-5 p-8 md:p-12 flex flex-col justify-center">
        <div className="text-[10px] uppercase tracking-widest2 text-gold-600">Featured Suite</div>
        <h3 className={`mt-3 text-3xl md:text-4xl font-display text-forest-700 leading-tight ${lang==='bn'?'font-bn':''}`}>
          {lang === 'en' ? room.name_en : room.name_bn}
        </h3>
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-warm">
          <Users className="w-4 h-4 text-gold-600" /> {room.capacity} {t.rooms.capacity}
        </div>
        <p className="mt-5 text-sm text-slate-warm leading-relaxed">
          {lang === 'en' ? room.description_en : room.description_bn}
        </p>

        <div className="mt-7 flex items-end gap-4">
          {hasOffer && (
            <div>
              <div className="text-[10px] uppercase tracking-widest2 text-slate-warm">{t.rooms.standard}</div>
              <div className="text-base text-slate-warm line-through">৳{room.price_bdt.toLocaleString()}</div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-widest2 text-gold-600">
              {hasOffer ? t.rooms.offer_label : t.rooms.standard}
            </div>
            <div className="text-4xl font-display text-forest-700 leading-none">
              ৳{(room.offer_price_bdt || room.price_bdt).toLocaleString()}
              <span className="text-sm text-slate-warm align-baseline ml-1">{t.rooms.per_night}</span>
            </div>
          </div>
        </div>
        {hasOffer && (
          <div className="mt-3 text-[11px] text-gold-700">{t.rooms.booked_via_ai}</div>
        )}

        <div className="mt-8 flex flex-wrap gap-1.5">
          {(room.amenities || []).slice(0, 5).map((a) => (
            <span key={a} className="text-[10px] uppercase tracking-widest2 text-forest-700 border border-forest-100 px-2.5 py-1 rounded-full">{a}</span>
          ))}
        </div>

        <button onClick={() => onPick(room.id)} data-testid="featured-reserve-btn"
                className="mt-8 group inline-flex items-center justify-center gap-2 bg-forest-700 text-cream-100 px-7 py-3.5 rounded-full text-sm hover:bg-forest-800 transition-all hover:gap-4 border border-gold-600/40 self-start">
          {t.rooms.view} <MoveRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </button>
      </div>
    </article>
  );
};

const ContactRow = ({ icon: Icon, label, value, href, testid }) => (
  <a href={href} className="flex items-center gap-4 group" data-testid={testid}>
    <div className="w-9 h-9 rounded-full border border-forest-200 flex items-center justify-center group-hover:border-gold-600 transition-colors">
      <Icon className="w-3.5 h-3.5 text-gold-600" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-widest2 text-gold-600">{label}</div>
      <div className="text-sm text-forest-700 group-hover:text-gold-700 transition-colors">{value}</div>
    </div>
  </a>
);
